import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Paper,
  alpha,
  useTheme,
  Divider,
  Chip,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccountTree as TreeIcon,
  Hub as HubIcon,
  Search as SearchIcon,
  Verified as VerifiedIcon,
  Psychology as PsychologyIcon,
  School as SchoolIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  AutoFixHigh as AutoFixIcon,
  BubbleChart as BubbleIcon,
  CompareArrows as CompareIcon,
  Visibility as VisibilityIcon,
  Science as ScienceIcon,
  MenuBook as BookIcon,
  LibraryBooks as LibraryIcon,
  Lightbulb as LightbulbIcon,
  FilterAlt as FilterIcon,
  Route as RouteIcon,
  DataObject as DataIcon,
  Layers as LayersIcon,
  Explore as ExploreIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// ─── Domain Data ────────────────────────────────────────────
const domains = [
  { name: 'Focus & Tools', count: 34, color: '#1565c0' },
  { name: 'Problem Solving', count: 29, color: '#6a1b9a' },
  { name: 'Self', count: 35, color: '#c62828' },
  { name: 'People', count: 32, color: '#2e7d32' },
  { name: 'Foundational', count: 19, color: '#e65100' },
  { name: 'Language', count: 38, color: '#00838f' },
  { name: 'Numbers', count: 44, color: '#4527a0' },
  { name: 'Physical', count: 8, color: '#558b2f' },
  { name: 'Strategic', count: 8, color: '#ad1457' },
  { name: 'Literacy', count: 29, color: '#f57c00' },
  { name: 'Communication', count: 9, color: '#0277bd' },
  { name: 'Technology', count: 2, color: '#455a64' },
  { name: 'Cognition', count: 1, color: '#5d4037' },
];

// ─── Three-Layer Architecture ───────────────────────────────
const architectureLayers = [
  {
    title: 'Relational Layer — "Safe Hands"',
    tech: 'MySQL',
    description: 'Raw attainment, student records, user accounts, and skill definitions. Deterministic, structured, auditable. The bedrock that never hallucinates.',
    icon: <StorageIcon />,
    color: '#1565c0',
  },
  {
    title: 'Semantic Layer — "Smart Minds"',
    tech: 'Weaviate Vector DB',
    description: 'Vectorised semantic links, analogy detection, and structural relationship mapping. 1,536-dimensional embeddings enable cross-curricular pattern discovery that no manual process could achieve.',
    icon: <BubbleIcon />,
    color: '#6a1b9a',
  },
  {
    title: 'Reasoning Layer — "The Architect"',
    tech: 'CHISG Logic (Go)',
    description: 'The rules engine that validates claims using Triangulation, Recursive Mastery, and the Echo Chamber Filter. Truth is computed, not assumed.',
    icon: <PsychologyIcon />,
    color: '#2e7d32',
  },
];

// ─── Component ──────────────────────────────────────────────
const SkillsMapLanding: React.FC = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* ── Hero Section ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, #004d40 0%, #00695c 40%, #009688 100%)`,
          color: 'white',
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 12 },
          px: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '80px',
            background: 'linear-gradient(to top right, #f5f7f9 50%, transparent 50%)',
          },
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                {/* Back to portfolio */}
                <Button
                  component="a"
                  href="/"
                  startIcon={<HomeIcon />}
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    textTransform: 'none',
                    alignSelf: 'flex-start',
                    '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Back to Portfolio
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TreeIcon sx={{ fontSize: { xs: 48, md: 64 } }} />
                  <Box>
                    <Typography variant="h2" component="h1" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                      The Skills Map
                    </Typography>
                    <Typography variant="subtitle1" sx={{ opacity: 0.85, mt: 0.5 }}>
                      Contextualised Hierarchical Iterative Semantic Groupings
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="h5" sx={{ opacity: 0.92, maxWidth: 620 }}>
                  A comprehensive database of development that maps the landscape of human competency. Whether for education or workforce planning, CHISG enables intelligent gap analysis, diagnostic insights, and personalized growth pathways.
                </Typography>

                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Chip label="579+ Skills" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                  <Chip label="1,120+ Semantic Links" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                  <Chip label="14 Domains" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                </Stack>

                <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to="/explore"
                    sx={{
                      bgcolor: 'white',
                      color: '#004d40',
                      fontWeight: 'bold',
                      px: 4,
                      '&:hover': { bgcolor: alpha('#ffffff', 0.9) },
                    }}
                  >
                    Explore the Graph
                  </Button>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={8}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: alpha(theme.palette.background.paper, 0.95),
                }}
              >
                <Typography variant="h6" color="primary" gutterBottom fontWeight="bold">
                  The Truth Decay Problem
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Large Language Models are powerful — but they hallucinate. They generate plausible-sounding
                  content with no guarantee of accuracy. In education, this is dangerous: a student who receives
                  confidently-stated misinformation doesn't just fail to learn — they actively learn the wrong thing.
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1.5}>
                  {[
                    { icon: <VerifiedIcon fontSize="small" />, text: 'CHISG grounds AI in verified knowledge structures — every claim is traceable to source.' },
                    { icon: <HubIcon fontSize="small" />, text: 'Skills connected by semantic links form a graph that LLMs must respect, not invent.' },
                    { icon: <FilterIcon fontSize="small" />, text: 'The Echo Chamber Filter distinguishes genuine consensus from repeated copying.' },
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{ color: 'primary.main', mt: 0.3 }}>{item.icon}</Box>
                      <Typography variant="body2">{item.text}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                  "Extract what sources claim. Tag where they came from. Let coherence emerge from the graph."
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Core Functions Section ── */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#f5f7f9' }}>
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Typography variant="h4" component="h2" textAlign="center" fontWeight="bold" gutterBottom>
            Assess. Diagnose. Plan.
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 5, maxWidth: 780, mx: 'auto' }}
          >
            CHISG is a functional database of development. It serves three primary functions in any learning context,
            allowing teachers, managers, and independent learners to measure capability, identify blockers, and plot efficient paths to mastery.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                icon: <DataIcon fontSize="large" />,
                title: 'Assess',
                description:
                  'A teacher (or evaluator) can provide a precise score (1–5) against standardized criteria. This creates a deterministic record of current capability — a "Database of Development" — replacing subjective judgment with auditable data.',
              },
              {
                icon: <CompareIcon fontSize="large" />,
                title: 'Diagnose',
                description:
                  'Do patterns of capabilities suggest a deeper problem? The graph identifies when struggles in one domain are actually root-cause failures in another, enabling targeted intervention rather than repeated failure.',
              },
              {
                icon: <RouteIcon fontSize="large" />,
                title: 'Plan',
                description:
                  'By establishing a desired end-point, the system plots the most efficient path of small, achievable goals (micro-credentials). It suggests immediate, relevant activities based on the student\'s current competency profile.',
              },
              {
                icon: <LayersIcon fontSize="large" />,
                title: 'Workforce & HR',
                description:
                  'Create a "Skills Profile" for a job role. Applicants can use their own personal Skills Map as a CV, providing verified evidence of their match against the role. "Distance to competency" becomes a measurable metric.',
              },
              {
                icon: <AutoFixIcon fontSize="large" />,
                title: 'The Database of Development',
                description:
                  'Background information, evidence, and assessment history accumulate to create a complete picture of development. This persistent record travels with the learner, solving the "black slate" problem at every transition.',
              },
              {
                icon: <SecurityIcon fontSize="large" />,
                title: 'Semantic Connectivity',
                description:
                  'A skill is defined by its connections. To assess a skill is to validate its links. The graph ensures that "Mastery" implies not just isolated knowledge, but the ability to support downstream concepts.',
              },
            ].map((card) => (
              <Grid item xs={12} md={6} key={card.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                        {card.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Teacher Workload & Curriculum ── */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: alpha(theme.palette.secondary.main, 0.04), borderY: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}` }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SchoolIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Zero-Friction Classroom Integration
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 'normal', mb: 2 }}>
              Reduces Workload Without Changing Curriculum
            </Typography>
          </Box>
          
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              <strong>CHISG does not dictate <em>what</em> you teach.</strong> It maps the skills students practice <em>on</em> your existing material.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              By separating skill acquisition from content delivery, the system automates the heavy lifting of differentiation and assessment 
              without requiring a rewrite of your curriculum. Students learn by practicing skills on the material you already use, while 
              the graph handles the tracking, gap analysis, and pathfinding in the background.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* ── Review Three-Layer Architecture ── */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Typography variant="h4" component="h2" textAlign="center" fontWeight="bold" gutterBottom>
            Three-Layer Architecture
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 2, maxWidth: 740, mx: 'auto' }}
          >
            CHISG separates what is <em>known</em> (deterministic) from what is <em>inferred</em> (probabilistic),
            and wraps both in a reasoning engine that validates claims before they reach the student.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 5, maxWidth: 600, mx: 'auto', fontStyle: 'italic' }}
          >
            The principle: never let a probabilistic system claim certainty it doesn't have.
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {architectureLayers.map((layer) => (
              <Grid item xs={12} md={4} key={layer.title}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderTop: `4px solid ${layer.color}`,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ color: layer.color }}>{layer.icon}</Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {layer.title}
                      </Typography>
                      <Chip label={layer.tech} size="small" variant="outlined" sx={{ fontSize: '0.7rem', mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, flexGrow: 1 }}>
                    {layer.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Domain Taxonomy ── */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#f5f7f9' }}>
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Typography variant="h4" component="h2" textAlign="center" fontWeight="bold" gutterBottom>
            14 Skill Domains
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 5, maxWidth: 880, mx: 'auto' }}
          >
            These domains are simply navigational subdivisions of an expansive (yet growing) set of skills. They are not rigid silos.
            You select the subset of skills relevant to your course or job role to create a bespoke profile from the master graph.
            Cross-domain links enable the analogy detection that makes CHISG unique.
          </Typography>

          <Grid container spacing={1.5} justifyContent="center">
            {domains.map((d) => (
              <Grid item xs={6} sm={4} md={3} key={d.name}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    border: `1px solid ${alpha(d.color, 0.2)}`,
                    borderRadius: 2,
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" sx={{ color: d.color }}>
                    {d.count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.name}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Paper
              elevation={0}
              sx={{
                display: 'inline-block',
                px: 4,
                py: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={4} divider={<Divider orientation="vertical" flexItem />}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary">579+</Typography>
                  <Typography variant="caption" color="text.secondary">Skills Mapped</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary">1,120+</Typography>
                  <Typography variant="caption" color="text.secondary">Semantic Links</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary">5,000+</Typography>
                  <Typography variant="caption" color="text.secondary">Indexed Objects</Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* ── The 6-Step Grounding Process ── */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Typography variant="h4" component="h2" textAlign="center" fontWeight="bold" gutterBottom>
            The Semantic Grounding Process
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 5, maxWidth: 700, mx: 'auto' }}
          >
            How CHISG prevents AI hallucination — every generated response passes through a six-stage
            verification pipeline before reaching the student.
          </Typography>

          <Grid container spacing={2}>
            {[
              {
                step: '1',
                title: 'Topic Detection',
                description: 'Identify which CHISG domain and skill cluster the query relates to. Map the student\'s question to the graph.',
                color: '#1565c0',
              },
              {
                step: '2',
                title: 'Semantic Link Retrieval',
                description: 'Retrieve all verified relationships from the knowledge graph. The LLM receives facts, not guesses.',
                color: '#6a1b9a',
              },
              {
                step: '3',
                title: 'Metadata Check',
                description: 'Inspect confidence scores, source boundaries, and explicit unknowns. "I don\'t know" is always a valid answer.',
                color: '#2e7d32',
              },
              {
                step: '4',
                title: 'Constrained Generation',
                description: 'The LLM generates a response anchored to verified links. It can explain and rephrase, but cannot invent new claims.',
                color: '#e65100',
              },
              {
                step: '5',
                title: 'Post-Generation Verification',
                description: 'Every claim in the output is cross-checked against the graph. Unverifiable statements are flagged or removed.',
                color: '#c62828',
              },
              {
                step: '6',
                title: 'Confidence Delivery',
                description: 'The student receives the response with confidence scores, source attributions, and explicit caveats where knowledge is incomplete.',
                color: '#00838f',
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.step}>
                <Box
                  sx={{
                    p: 2.5,
                    borderLeft: `4px solid ${item.color}`,
                    bgcolor: alpha(item.color, 0.04),
                    borderRadius: '0 8px 8px 0',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: item.color,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '0.85rem',
                      }}
                    >
                      {item.step}
                    </Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── The "Faking It" Engine ── */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: alpha('#004d40', 0.03),
          borderTop: `1px solid ${alpha('#004d40', 0.08)}`,
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Typography variant="h4" component="h2" textAlign="center" fontWeight="bold" gutterBottom>
            The Bootstrap Paradox — "Faking It" to Truth
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 5, maxWidth: 780, mx: 'auto' }}
          >
            Every knowledge system faces a cold-start problem: you need validated knowledge to build the
            graph, but you need the graph to validate knowledge. CHISG solves this with a three-phase
            honesty protocol.
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%', borderTop: '4px solid #f57c00' }}>
                <Typography variant="overline" sx={{ color: '#f57c00', fontWeight: 'bold' }}>Phase 1</Typography>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Expert Bootstrap</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Unsourced expert heuristics — 12 years of classroom observation — treated as <strong>"Foundational
                  Hypotheses."</strong> The system is honest about what it knows and what it's guessing. Every
                  bootstrap entry is tagged as such.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%', borderTop: '4px solid #2e7d32' }}>
                <Typography variant="overline" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>Phase 2</Typography>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Empirical Backfilling</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Observed intervention results validate or invalidate hypotheses. When a predicted skill relationship
                  produces measurable learning gains, it upgrades from hypothesis to <strong>empirically-validated</strong>. 
                  Failed predictions are flagged, not hidden.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%', borderTop: '4px solid #1565c0' }}>
                <Typography variant="overline" sx={{ color: '#1565c0', fontWeight: 'bold' }}>Phase 3</Typography>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Academic Grounding</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Retroactive sourcing against peer-reviewed literature. What started as a teacher's intuition
                  is either confirmed by research (trust score rises) or contradicted (entry is revised). The
                  graph <strong>gets more honest over time</strong>, never less.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── How CHISG Connects to the Ecosystem ── */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Typography variant="h4" component="h2" textAlign="center" fontWeight="bold" gutterBottom>
            The Nervous System of the Ecosystem
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 5, maxWidth: 740, mx: 'auto' }}
          >
            CHISG doesn't exist in isolation — it's the knowledge backbone that connects every other
            tool in the ESP Thinking Portfolio. Each project reads from and writes to the graph.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                title: 'ETP Integration',
                description: 'Each CHISG skill has an etp_modulation field indicating which emotional spectrum gets stressed when learning it. The knowledge graph knows not just WHAT to teach, but WHY it triggers certain students.',
                icon: <PsychologyIcon />,
              },
              {
                title: 'LAO Adaptive Revision',
                description: 'GCSE revision content is generated from CHISG skill definitions, ensuring every flashcard and summary is traceable to verified knowledge. Gap analysis identifies exactly which skills block progress.',
                icon: <BookIcon />,
              },
              {
                title: 'SkillsMarkbook',
                description: 'Teachers log competency against CHISG skills in real-time. Each observation feeds back into the graph, strengthening trust scores for well-evidenced relationships.',
                icon: <SchoolIcon />,
              },
              {
                title: 'SkillsProfileParent',
                description: 'Complex graph data is translated into human narratives for parents. "Your child has mastered 12/15 prerequisites for algebra" is more useful than a percentage.',
                icon: <VisibilityIcon />,
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} key={item.title}>
                <Card elevation={1} sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ color: 'primary.main' }}>{item.icon}</Box>
                      <Typography variant="subtitle1" fontWeight="bold">{item.title}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── What Makes CHISG Different ── */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: `linear-gradient(135deg, ${alpha('#004d40', 0.06)} 0%, ${alpha('#009688', 0.06)} 100%)`,
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Typography variant="h4" component="h2" textAlign="center" fontWeight="bold" gutterBottom>
            What Makes CHISG Different
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            {[
              'Links define skills — relationships are the substance, not metadata',
              'Triangulation-based trust — truth through structural redundancy',
              'The Echo Chamber Filter — distinguishes consensus from copying',
              'Automatic cross-curricular analogy detection',
              '"I don\'t know" is always a valid answer — explicit uncertainty',
              'Three-phase bootstrap — honest about what it\'s guessing',
              'ETP modulation — the graph knows WHY skills trigger students',
              'Recursive mastery — successful interventions strengthen the graph',
              'Safe Hands + Smart Minds — deterministic data, probabilistic reasoning',
              'Every claim traceable to source material with provenance chain',
              '5,000+ indexed objects across skills, links, and documentation',
              'Grounded in 12+ years of classroom evidence, not academic theory',
            ].map((point) => (
              <Grid item xs={12} sm={6} md={4} key={point}>
                <Box sx={{ display: 'flex', gap: 1.5, p: 1.5 }}>
                  <Box sx={{ color: '#009688', mt: 0.25 }}>✓</Box>
                  <Typography variant="body2">{point}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA Section ── */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <ExploreIcon sx={{ fontSize: 56, color: '#009688', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Explore the Knowledge Graph
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            The interactive graph explorer lets you navigate skill relationships, discover cross-curricular
            connections, and see how 579+ skills connect across 14 domains. Click any node to drill into its
            semantic links, prerequisites, and downstream capabilities.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/explore"
            startIcon={<HubIcon />}
            sx={{
              px: 5,
              py: 1.5,
              fontWeight: 'bold',
              bgcolor: '#009688',
              '&:hover': { bgcolor: '#00796b' },
            }}
          >
            Launch Skills Map Explorer →
          </Button>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#004d40', color: 'rgba(255,255,255,0.7)' }}>
        <Typography variant="body2">
          CHISG Skills Map — Contextualised Hierarchical Iterative Semantic Groupings — Part of the ESP Thinking Portfolio
        </Typography>
      </Box>
    </Box>
  );
};

export default SkillsMapLanding;
